import { defineStore } from "pinia";
import type { DRDrawing, DRLocalDrawingMeta } from "@/types";
import { computed, ref, watch } from "vue";
import { liveQuery } from "dexie";
import { v4 as uuidV4 } from "uuid";
import { remove } from "lodash";
import { syncer } from "@/repositories/Syncer";
import APIClient from "@/APIClient";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { drawingRepository } from "@/repositories/drawing.repository";
import { Monitor } from "@/utils/monitor";
import { useDeviceState } from "@/stores/DeviceState";
import {
  removeAccents,
  removeSpecialCharsForOcsFiles,
} from "@/utils/misc.utils";
import { Logger } from "@/logger";
import { queryClient } from "@/stores/queries/query-client";
import { QueryKey } from "./queries/query-keys";
import UserState from "./UserState";
import { isDrawingSynced } from "@/utils/drawings.utils";

type TrackerStatus = "uploading" | "done" | "failed";

export type UploadProgressHook = (progress: number) => void;

export type Tracker = {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  progressDisplay: number;
  status: TrackerStatus;
  error?: string;
  projectId: number;
  drawingId?: number;
};

function renameFile(originalFile: File, newName: string) {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
}

export const useDrawingStore = defineStore("fileStore", () => {
  const organizationId = computed(() => UserState.data?.organization?.id);

  const localDrawings = ref<DRLocalDrawingMeta[] | null>(null);

  const deviceState = useDeviceState();

  async function syncDrawings() {
    await deviceState.checkIfOnline();
    if (!deviceState.isOnline) return;
    try {
      await syncer.autoSync();
    } catch(e) {
      Logger.error(`fileStore.syncDrawingStat() : error ${JSON.stringify(e)}`);
    }
  }

  const syncMonitor = new Monitor(syncDrawings, { intervalInMs: 1000 * 10 });

  async function retrySyncDrawing(drawingId: number) {
    if (!deviceState.isOnline) return;
    try {
      const localDrawing = await localDrawingRepository.getByIdOrThrow(
        drawingId
      );
      if (localDrawing.error) {
        await localDrawingRepository.clearDrawingError(drawingId);
        await syncMonitor.trigger();
      }
    } catch(e) {
      Logger.error(`fileStore.syncDrawingStat() : error ${JSON.stringify(e)}`);
    }
  }

  /**
   * Guarded equivalent of `syncDrawings()` for external one-off triggers (e.g.
   * DrawingCanvas.vue on unmount) — shares `syncMonitor`'s reentrancy lock so it
   * can't run concurrently with the periodic tick or with AuthLayout's
   * `startAutoSync()` (both fire around the same moment when leaving the
   * canvas), which otherwise race unguarded on the same Dexie tables.
   */
  function triggerSync(): Promise<void> {
    return syncMonitor.trigger();
  }

  const trackers = ref<Tracker[]>([]);

  const removeTracker = (id: string) => {
    remove(trackers.value, { id });
  };

  const refreshData = () => {
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DRAWINGS],
      });
    }, 500);
  };

  /**
   * MÉTADONNÉES UNIQUEMENT (`fetchAllMetaByNewest`) : ce liveQuery se ré-exécute à
   * chaque écriture sur `db.drawings` — donc à chaque `saveDraft` — et son résultat
   * est retenu en permanence par `localDrawings`. Avec `fetchAllByNewest()` c'était
   * l'intégralité
   * des DWG locaux (plusieurs Mo par dessin) qui restait vivante en RAM et se
   * re-désérialisait à chaque autosave. Qui a besoin d'un binaire le recharge
   * ponctuellement via `localDrawingRepository.getByIdOrThrow(id)`.
   */
  const localDataObservable = liveQuery<DRLocalDrawingMeta[]>(() =>
    localDrawingRepository.fetchAllMetaByNewest()
  );

  // Abonnement conservé pour pouvoir le suspendre pendant l'outil dessin
  // (cf. suspendLocalWatch).
  let localSubscription: { unsubscribe(): void } | null = null;
  const watchLocalDrawings = () => {
    localSubscription = localDataObservable.subscribe({
      next: (result: DRLocalDrawingMeta[]) => {
        localDrawings.value = result;
      },
      // Base inaccessible (VersionError, corruption…) : sans ce handler l'erreur
      // était avalée et la liste restait simplement vide.
      error: (e: unknown) => {
        Logger.error(`fileStore.ts : local drawings liveQuery failed — ${e}`);
        Logger.captureException(e, { where: "fileStore.localDataObservable" });
      },
    });
  };
  watchLocalDrawings();

  /**
   * Suspend la relecture automatique des dessins locaux. Le liveQuery relit TOUTE
   * la table, binaires compris, après chaque écriture — donc après chaque
   * sauvegarde du dessin ouvert, au pire moment : scène WebAssembly en mémoire,
   * overlay de sauvegarde affiché. Rien dans l'outil dessin n'en a besoin (cf.
   * DrawingCanvas).
   */
  function suspendLocalWatch() {
    localSubscription?.unsubscribe();
    localSubscription = null;
  }

  /** Reprend la relecture, avec une lecture immédiate de l'état courant. */
  function resumeLocalWatch() {
    if (localSubscription) return;
    watchLocalDrawings();
  }

  watch(
    organizationId,
    async () => {
      localDrawings.value = await localDrawingRepository.fetchAllMetaByNewest();
    },
    {
      immediate: true,
    }
  );

  const setTrackerProgress = (trackerId: string, progress: number) => {
    const tracker = trackers.value.find((f) => f.id === trackerId);
    if (!tracker) return;

    tracker.progress = progress;
    tracker.progressDisplay = Math.round(progress * 100);
  };

  const setTrackerDone = (trackerId: string, drawing: DRDrawing) => {
    const tracker = trackers.value.find((f) => f.id === trackerId);
    if (!tracker) return;

    tracker.status = "done";
    tracker.progress = 1;
    tracker.progressDisplay = 100;
    tracker.drawingId = drawing.id;

    refreshData();

    // Remove tracker after showing success briefly
    setTimeout(() => {
      removeTracker(trackerId);
    }, 3000);
  };

  const setTrackerError = (trackerId: string, error: string) => {
    const tracker = trackers.value.find((f) => f.id === trackerId);
    if (!tracker) return;

    tracker.status = "failed";
    tracker.error = error;
  };

  /** Rattachement d'un plan fraîchement importé à un ouvrage. */
  const linkDrawingToDiagnostic = async (
    drawingId: number,
    diagnosticId: number
  ) => {
    try {
      const updated = await APIClient.updateDrawing(drawingId, {
        diagnosticId,
      });
      await drawingRepository.update(drawingId, {
        diagnostic: updated.diagnostic ?? null,
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
    } catch (e) {
      Logger.error(
        `fileStore.ts : could not link drawing ${drawingId} to diagnostic ${diagnosticId}`
      );
      console.error(e);
    }
  };

  /**
   * @param options.diagnosticId rattache les plans créés à un ouvrage dès leur
   * création (import depuis la page ouvrage), sans repasser par l'outil dessin.
   */
  const uploadFiles = async (
    files: File[],
    projectId: number,
    options?: { diagnosticId?: number }
  ) => {
    const renamedFiles = files.map((file) => {
      const splitted = file.name.split(".");
      const extension = splitted.pop();
      const normalized = removeSpecialCharsForOcsFiles(
        removeAccents(splitted.join("."))
      );

      return renameFile(file, `${normalized}.${extension}`);
    });

    const newTrackers = Array.from(renamedFiles).map(
      (file): Tracker => ({
        id: uuidV4(),
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        progressDisplay: 0,
        status: "uploading",
        projectId,
      })
    );

    trackers.value = trackers.value.concat(newTrackers);

    const progressHooks = newTrackers.map((tracker) => ({
      onProgress: (progress: number) =>
        setTrackerProgress(tracker.id, progress),
      onDrawingCreated: (drawing: DRDrawing) => {
        setTrackerDone(tracker.id, drawing);
        if (options?.diagnosticId) {
          linkDrawingToDiagnostic(drawing.id, options.diagnosticId);
        }
      },
      onError: (error: string) => setTrackerError(tracker.id, error),
    }));

    await syncer.uploadManyDwg(renamedFiles, projectId, progressHooks);
  };

  function startAutoSync() {
    syncMonitor.start();
  }
  function stopAutoSync() {
    syncMonitor.stop();
  }

  function clearDrawings() {
    localDrawings.value = [];
    trackers.value = [];
  }

  return {
    // data
    localDrawings,
    trackers,

    // methods
    uploadFiles,
    removeTracker,
    stopAutoSync,
    startAutoSync,
    clearDrawings,
    syncDrawings,
    retrySyncDrawing,
    triggerSync,
    suspendLocalWatch,
    resumeLocalWatch,

    // computed
    areAllSynced: computed(() =>
      localDrawings.value
        ? localDrawings.value.every((e) => isDrawingSynced(e))
        : false
    ),
    unsyncCount: computed(() =>
      localDrawings.value
        ? localDrawings.value.filter((e) => !isDrawingSynced(e)).length
        : 0
    ),
    erroredCount: computed(() =>
      localDrawings.value
        ? localDrawings.value.filter((e) => !!e.error).length
        : 0
    ),
    localIds: computed(() =>
      localDrawings.value ? localDrawings.value.map((d) => d.id) : undefined
    ),
  };
});
